<script setup lang="ts">
/**
 * Contact 병합(중복 정리) 다이얼로그 — 2-Step 스테퍼
 *
 * 리스트 페이지에서 체크박스로 골라 넘겨준 컨택트 스냅샷(`preselected`) 만 사용한다.
 * 다이얼로그는 자체적으로 Contact 목록을 로드하지 않아 자원 낭비 없음.
 *
 * Step 1 (Select Main): preselected 중 남길 메인 1개를 라디오로 선택 (기본값: 첫 항목).
 *                       나머지는 자동으로 sub 후보가 됨.
 * Step 2 (Confirm & Merge): 각 sub 가 참조되는 엔티티/역할 을 표로 보여주고,
 *                           "정말 정리할래요?" 최종 확인 → 실행.
 *
 * 실행 시 backend POST /Contact/merge 가:
 *   L_OFFICER / L_SHAREHOLDER_DETAIL / L_CONTROLLERMANAGE / L_BENEFICIALOWNER 의
 *   sub CONTACT_CD 를 main 으로 재지정하고,
 *   sub L_GLOBAL_CONTACT.IS_DEL = 1 로 soft delete.
 */

import { ref, computed, watch, onMounted } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useDialog } from '@/shared/composables/useDialog';
import { contactsApi } from '../api/contactsApi';
import type { ContactDeleteBlocker } from '../types/contact.types';

type ContactRow = {
    CONTACT_CD: string;
    CONTACT_NAME?: string;
    FIRST_NAME?: string;
    SURNAME?: string;
    CATEGORY_CD?: string;
    CONTACT_EMAIL?: string;
    CONTACT_REG_NO?: string;
};

interface Props {
    modelValue: boolean;
    /** 리스트에서 체크박스로 고른 컨택트 (2건 이상). 열릴 때마다 스냅샷으로 받음. */
    preselected: ContactRow[];
}
const props = defineProps<Props>();
const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    'merged': [];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});

const { alert, confirm } = useDialog();

const step = ref<1 | 2>(1);
const isLoadingPreview = ref(false);
const isSubmitting = ref(false);

/** 다이얼로그가 열렸을 때 스냅샷으로 고정된 후보 (props.preselected 는 참조로 바뀔 수 있어 복사) */
const candidates = ref<ContactRow[]>([]);

/** 메인 선택 CONTACT_CD (Step 1) */
const mainContactCd = ref<string>('');

type SubPreview = {
    contactCd: string;
    name: string;
    contactRegNo?: string;
    references: ContactDeleteBlocker[];
};
/** 서버 preview 응답 (Step 2) */
const previewSubs = ref<SubPreview[]>([]);

function displayName(c: ContactRow): string {
    return (
        (c.CONTACT_NAME || '').trim()
        || [c.FIRST_NAME, c.SURNAME].filter(Boolean).join(' ').trim()
        || c.CONTACT_CD
    );
}

function categoryLabel(c?: ContactRow | null): string {
    if (!c) return '';
    return c.CATEGORY_CD === 'B09001'
        ? (t('contact.Corporate') || 'Corporate')
        : (t('contact.Individual') || 'Individual');
}

function roleLabel(b: ContactDeleteBlocker): string {
    const key = String(b.role || '').trim();
    // Officer 는 실제 POSITION_NAME 을 그대로 사용
    if (key === 'Officer' && b.position) return b.position;

    const roleMap: Record<string, string> = {
        Officer: t('contact.Officer') || 'Officer',
        Shareholder: t('contact.Shareholder') || 'Shareholder',
        Controller: t('contact.Controller') || 'Controller',
        'Beneficial Owner': t('contact.Beneficial Owner') || 'Beneficial Owner',
    };
    // Beneficial Owner: position 이 'Legal Owner' 이면 "Beneficial Owner (Legal Owner)" 로 구분 표시.
    if (key === 'Beneficial Owner' && b.position) {
        const posMap: Record<string, string> = {
            'Legal Owner': t('contact.Legal Owner') || 'Legal Owner',
        };
        const posLabel = posMap[b.position] || b.position;
        return `${roleMap['Beneficial Owner']} (${posLabel})`;
    }
    return roleMap[key] || key;
}

const mainContact = computed<ContactRow | null>(
    () => candidates.value.find((c) => c.CONTACT_CD === mainContactCd.value) || null,
);
const subContacts = computed<ContactRow[]>(
    () => candidates.value.filter((c) => c.CONTACT_CD !== mainContactCd.value),
);

/**
 * 후보 간 CATEGORY_CD 검증 — 전부 같은 카테고리여야 서버에서 병합 가능.
 * 다르면 Step 1 에서 이유를 안내하고 다음 단계로 못 넘어가게 함.
 */
const categoryMismatch = computed(() => {
    const cats = new Set(
        candidates.value
            .map((c) => String(c.CATEGORY_CD || '').trim())
            .filter((v) => v !== ''),
    );
    return cats.size > 1;
});

async function goToStep2() {
    if (!mainContactCd.value || subContacts.value.length === 0) return;
    if (categoryMismatch.value) return;
    isLoadingPreview.value = true;
    try {
        const resp = (await contactsApi.getMergePreview({
            mainContactCd: mainContactCd.value,
            subContactCds: subContacts.value.map((c) => c.CONTACT_CD),
        })) as any;
        previewSubs.value = (resp?.subs || []) as SubPreview[];
        step.value = 2;
    } catch (err: any) {
        console.error('[ContactMergeDialog] preview failed', err);
        await alert({
            title: t('contact.Error') || 'Error',
            message: err?.message || (t('contact.Merge Preview Failed') || 'Failed to load merge preview'),
            type: 'error',
        });
    } finally {
        isLoadingPreview.value = false;
    }
}

async function executeMerge() {
    const proceed = await confirm({
        title: t('contact.Confirm Merge') || 'Confirm Merge',
        message:
            (t('contact.This action cannot be undone. Proceed?')
                || 'This action cannot be undone. All references from the duplicate contacts will be moved to the main contact and the duplicates will be hidden. Proceed?'),
        type: 'warning',
    });
    if (!proceed) return;

    isSubmitting.value = true;
    try {
        await contactsApi.mergeContacts({
            mainContactCd: mainContactCd.value,
            subContactCds: subContacts.value.map((c) => c.CONTACT_CD),
        });
        await alert({
            title: t('contact.Success') || 'Success',
            message: (t('contact.Merge Completed') || 'Merge completed successfully'),
            type: 'success',
        });
        emit('merged');
        closeDialog();
    } catch (err: any) {
        console.error('[ContactMergeDialog] merge failed', err);
        await alert({
            title: t('contact.Error') || 'Error',
            message: err?.response?.data?.error?.message
                || err?.message
                || (t('contact.Merge Failed') || 'Failed to merge contacts'),
            type: 'error',
        });
    } finally {
        isSubmitting.value = false;
    }
}

function closeDialog() {
    emit('update:modelValue', false);
}

/** 열릴 때마다 상태를 초기화 + 첫 항목을 메인 기본값으로 */
watch(
    () => props.modelValue,
    (open) => {
        if (!open) return;
        candidates.value = Array.isArray(props.preselected)
            ? props.preselected.filter((c) => c && c.CONTACT_CD).map((c) => ({ ...c }))
            : [];
        step.value = 1;
        previewSubs.value = [];
        mainContactCd.value = candidates.value[0]?.CONTACT_CD || '';
    },
    { immediate: false },
);
</script>

<template>
    <v-dialog
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
        max-width="960px"
        width="95%"
        persistent
    >
        <v-card rounded="md">
            <v-card-title class="d-flex align-center gap-2 pa-4">
                <v-icon color="primary">mdi-merge</v-icon>
                <span class="text-h6">{{ t('contact.Consolidate Contacts') || 'Consolidate Contacts' }}</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="closeDialog" />
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4 pb-0">
                <v-stepper :model-value="step" hide-actions flat class="merge-stepper">
                    <v-stepper-header>
                        <v-stepper-item
                            :value="1"
                            :complete="step > 1"
                            :editable="step > 1"
                            :title="t('contact.Step 1: Select Main') || 'Step 1: Select Main'"
                            :subtitle="t('contact.Choose the contact to keep') || 'Choose the contact to keep'"
                            @click="step = 1"
                        />
                        <v-divider />
                        <v-stepper-item
                            :value="2"
                            :title="t('contact.Step 2: Confirm & Merge') || 'Step 2: Confirm & Merge'"
                            :subtitle="t('contact.Review affected entities') || 'Review affected entities'"
                        />
                    </v-stepper-header>
                </v-stepper>
            </v-card-text>

            <v-divider />

            <!-- Empty state: preselected < 2 -->
            <v-card-text v-if="candidates.length < 2" class="pa-6 text-center text-medium-emphasis">
                {{ t('contact.Select at least two contacts from the list to merge.') || 'Select at least two contacts from the list to merge.' }}
            </v-card-text>

            <!-- Step 1: pick main -->
            <v-card-text v-else-if="step === 1" class="pa-4" style="max-height: 60vh; overflow-y: auto;">
                <div class="text-body-2 text-medium-emphasis mb-3">
                    {{ t('contact.Choose which contact to keep. The others will be merged into it.') || 'Choose which contact to keep. The others will be merged into it.' }}
                </div>
                <v-alert
                    v-if="categoryMismatch"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                >
                    {{ t('contact.Selected contacts have different categories (Individual/Corporate). Please select same-category contacts only.') || 'Selected contacts have different categories (Individual/Corporate). Please select same-category contacts only.' }}
                </v-alert>
                <v-radio-group
                    :model-value="mainContactCd"
                    @update:model-value="(v: any) => (mainContactCd = String(v || ''))"
                    hide-details
                >
                    <v-table density="compact" class="merge-table">
                        <thead>
                            <tr>
                                <th style="width: 60px;">{{ t('contact.Main') || 'Main' }}</th>
                                <th>{{ t('contact.Category') || 'Category' }}</th>
                                <th>{{ t('contact.Name') || 'Name' }}</th>
                                <th>{{ t('contact.Email') || 'Email' }}</th>
                                <th>{{ t('contact.Registration Number') || 'Registration Number' }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="c in candidates"
                                :key="c.CONTACT_CD"
                                @click="mainContactCd = c.CONTACT_CD"
                                :class="{ 'row-main': c.CONTACT_CD === mainContactCd, 'row-sub': c.CONTACT_CD !== mainContactCd }"
                                style="cursor: pointer;"
                            >
                                <td>
                                    <v-radio :value="c.CONTACT_CD" hide-details density="compact" />
                                </td>
                                <td>
                                    <v-chip :color="c.CATEGORY_CD === 'B09002' ? 'primary' : 'secondary'" size="x-small">
                                        {{ categoryLabel(c) }}
                                    </v-chip>
                                </td>
                                <td>{{ displayName(c) }}</td>
                                <td>{{ c.CONTACT_EMAIL || '-' }}</td>
                                <td>{{ c.CONTACT_REG_NO || '-' }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-radio-group>

                <div class="mt-3 text-caption text-medium-emphasis">
                    {{ t('contact.The rows shown as "sub" will be merged into the main and hidden.') || 'The rows shown as "sub" will be merged into the main and hidden.' }}
                </div>
            </v-card-text>

            <!-- Step 2: Confirm -->
            <v-card-text v-else class="pa-4" style="max-height: 60vh; overflow-y: auto;">
                <v-alert v-if="mainContact" type="success" variant="tonal" density="compact" class="mb-3">
                    <div class="d-flex align-center gap-2 flex-wrap">
                        <strong>{{ t('contact.Main (kept)') || 'Main (kept)' }}:</strong>
                        <span>{{ displayName(mainContact) }}</span>
                        <span v-if="mainContact.CONTACT_REG_NO" class="text-medium-emphasis">
                            ({{ mainContact.CONTACT_REG_NO }})
                        </span>
                    </div>
                </v-alert>
                <v-progress-linear v-if="isLoadingPreview" indeterminate color="primary" />

                <div v-for="sub in previewSubs" :key="sub.contactCd" class="merge-sub-card mb-3">
                    <div class="d-flex align-center gap-2 pa-3" style="background: rgba(0,0,0,0.03);">
                        <v-icon color="error" size="small">mdi-account-remove</v-icon>
                        <strong>{{ sub.name }}</strong>
                        <span v-if="sub.contactRegNo" class="text-medium-emphasis">({{ sub.contactRegNo }})</span>
                        <v-spacer />
                        <v-chip size="x-small" color="error" variant="tonal">
                            {{ sub.references.length }} {{ t('contact.linked entity/entities') || 'linked entity/entities' }}
                        </v-chip>
                    </div>
                    <v-table v-if="sub.references.length > 0" density="compact">
                        <thead>
                            <tr>
                                <th>{{ t('contact.Entity') || 'Entity' }}</th>
                                <th>{{ t('contact.Role') || 'Role' }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(ref, idx) in sub.references" :key="`${sub.contactCd}-${idx}`">
                                <td>{{ ref.entityName || ref.entityCd }}</td>
                                <td>{{ roleLabel(ref) }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                    <div v-else class="pa-3 text-medium-emphasis text-caption">
                        {{ t('contact.No linked entities — will simply be hidden.') || 'No linked entities — will simply be hidden.' }}
                    </div>
                </div>
            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-4">
                <v-btn v-if="step === 2" variant="text" @click="step = 1">
                    <v-icon start>mdi-chevron-left</v-icon>
                    {{ t('contact.Back') || 'Back' }}
                </v-btn>
                <v-spacer />
                <v-btn variant="text" @click="closeDialog">
                    {{ t('contact.Cancel') || 'Cancel' }}
                </v-btn>
                <v-btn
                    v-if="step === 1"
                    v-permission-button
                    data-menu-key="/Contact"
                    data-button-name="Review Merge"
                    color="primary"
                    variant="flat"
                    :disabled="candidates.length < 2 || !mainContactCd || categoryMismatch"
                    :loading="isLoadingPreview"
                    @click="goToStep2"
                >
                    {{ t('contact.Review') || 'Review' }}
                    <v-icon end>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn
                    v-else
                    v-permission-button
                    data-menu-key="/Contact"
                    data-button-name="Merge Now"
                    color="error"
                    variant="flat"
                    :loading="isSubmitting"
                    @click="executeMerge"
                >
                    <v-icon start>mdi-merge</v-icon>
                    {{ t('contact.Merge Now') || 'Merge Now' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss">
.merge-stepper :deep(.v-stepper-item__title) {
    font-size: 13px;
    font-weight: 600;
}
.merge-stepper :deep(.v-stepper-item__subtitle) {
    font-size: 11px;
}
.merge-table :deep(thead th) {
    font-size: 12px;
    font-weight: 700;
}
.merge-table :deep(tbody td) {
    font-size: 13px;
}
/* Step 1 시각 대비: 선택된 메인 row 강조, 나머지는 sub 로 옅게 */
.merge-table :deep(tbody tr.row-main) {
    background: rgba(27, 132, 255, 0.08);
}
.merge-table :deep(tbody tr.row-sub) {
    background: rgba(0, 0, 0, 0.02);
}
.merge-sub-card {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    overflow: hidden;
}
</style>
